import React, {Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../shared/hoc/createAdminPage'
import {
    NewAgentReservationForDuration,
    NewAgentReservationForOvernight,
    NewManagerReservationForDuration,
    NewManagerReservationForOvernight,
    ReservationsRepositoryForAdmin,
} from './ReservationsRepositoryForAdmin'
import {Nullable} from '../../../app/TypeHelpers'
import {CASH_PAYMENT_STATUS, MESSAGE_TYPE, PAYMENT_TYPE} from '../../../CodeMaster'
import {AdminReserveLocationState, AgentReservationInfo, ManagerReservationInfo} from './AdminReserveInterfaces'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {AdminAgentReserveSteps, AdminManagerReserveSteps, AdminReserveStepper} from './AdminReserveStepper'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../shared/hoc/withAdminPageTemplate'
import {BackLink} from '../../../styledComponents/adminPage/Links'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import lsHelper from './AdminReserveLocationStateHelper'
import {AdminReserveDetailsViewProps} from './AdminReserveComponents/ARDetailsView'
import FacilityForReservation from '../../facilities/FacilityForReservation'
import {
    PaymentInformation,
    PaymentInformationForReservation,
    ReservationOption,
    ReserveFacilityModelPaymentObserver,
} from '../ReserveFacilityModel'
import {valueStringOrNull} from '../../../codeMasterHelper/CodeMasterHelper'
import {FacilityRentalType, ReservationClass} from '../ReservationInterfaces'
import {updatePaymentInfo} from './AdminReserveHelper'
import {Messages} from '../../../Messages'
import {ApiResponse} from '../../../network/response/ApiResponse'
import {SelectedUserList} from '../../../__NoAutomatedTests/components/facilityReserve/repository/NatGetFacilityReserve'
import NatAdminCommonAcceptedWarningComponent
    from '../../../__NoAutomatedTests/components/common/admin/NatAdminCommonAcceptedWarningComponent'
import {AcceptedWarning, Warning} from '../../../__NoAutomatedTests/components/common/admin/NatAcceptedWarning'
import {Box} from '@mui/material'
import {Location} from 'react-router-dom'

interface Props {
    classification: ReservationClass
    condominiumCd: string
    reservationsRepoForAdmin: ReservationsRepositoryForAdmin
    location: Location<AdminReserveLocationState>
    renderDetailsView: (props: {
        data: AdminReserveDetailsViewProps,
        observer?: ReserveFacilityModelPaymentObserver,
    }) => ReactNode
}

interface State {
    cashPaymentStatus: Nullable<CASH_PAYMENT_STATUS>
    paymentInfo: Nullable<PaymentInformation>
    registerWarnings: Warning[]
    acceptedAllWarnings: boolean
}

const KEY_PREVIOUS_PAGE_URL: string = 'AdminReserveConfirmPage.previousPageUrl'
const KEY_PREVIOUS_PAGE_NAME: string = 'AdminReserveConfirmPage.previousPageName'

class AdminReserveConfirmPage
    extends Component<Props & AdminPagePropsSet, State>
    implements ReserveFacilityModelPaymentObserver {

    private locationState: AdminReserveLocationState
    private previousPageUrl: string
    private previousPageName: string
    private facilityData: FacilityForReservation
    private hasOptions: boolean

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        if (!this.props.location || !this.props.location.state || !this.props.location.state.reservationInfo) {
            throw Error('前画面からの引き継ぎ情報がありません。画面遷移が不正です。')
        }
        const {previousPageUrl, previousPageName, ...rest} = this.props.location.state
        this.locationState = lsHelper.fromObject(rest)

        if (previousPageUrl !== undefined && previousPageName !== undefined) {
            this.previousPageUrl = previousPageUrl
            this.previousPageName = previousPageName
            SessionStorageHelper.setItem(KEY_PREVIOUS_PAGE_URL, previousPageUrl)
            SessionStorageHelper.setItem(KEY_PREVIOUS_PAGE_NAME, previousPageName)
        } else {
            const url: Nullable<string> = SessionStorageHelper.getStringItemOrNull(KEY_PREVIOUS_PAGE_URL)
            const name: Nullable<string> = SessionStorageHelper.getStringItemOrNull(KEY_PREVIOUS_PAGE_NAME)
            if (!url || !name) {
                throw Error('前画面のリンク情報がありません。画面遷移が不正です。')
            }
            this.previousPageUrl = url || ''
            this.previousPageName = name || ''
        }

        if (this.props.classification !== this.locationState.reservationInfo.classification) {
            throw Error('渡された予約情報が画面と合っていません。画面遷移が不正です。')
        }

        if (this.locationState.facilityData === null) {
            throw Error('施設情報がありません。画面遷移が不正です。')
        }
        this.facilityData = this.locationState.facilityData
        this.hasOptions = this.locationState.facilityData.facilityOptions.length > 0

        let cashPaymentStatus: Nullable<CASH_PAYMENT_STATUS> = null
        if (this.locationState.reservationInfo.classification === ReservationClass.AGENT) {
            const {reservationInfo} = this.locationState
            const hasCashPaymentOption: boolean = reservationInfo.reservationOptionInfos === null ? false :
                undefined !== reservationInfo.reservationOptionInfos
                    .find(info => info.paymentType === PAYMENT_TYPE.CASH)
            const hasCashPaymentFacility: boolean = (reservationInfo.paymentType === PAYMENT_TYPE.CASH)
            cashPaymentStatus = (hasCashPaymentOption || hasCashPaymentFacility) ? CASH_PAYMENT_STATUS.YET_PAID : null
        }

        this.state = {cashPaymentStatus, paymentInfo: null, registerWarnings: [], acceptedAllWarnings: true}

        this.handleOnChangeAARCPaymentView = this.handleOnChangeAARCPaymentView.bind(this)
        this.handleClickPreviousPage = this.handleClickPreviousPage.bind(this)
        this.handleClickSubmit = this.handleClickSubmit.bind(this)
        this.changeAcceptedWarnings = this.changeAcceptedWarnings.bind(this)
    }

    onChangePayment(paymentProps: PaymentInformation): void {
        let update: Nullable<PaymentInformation>
        if (this.locationState.reservationInfo.classification === ReservationClass.AGENT) {
            update = updatePaymentInfo(paymentProps, this.locationState.reservationInfo)
        } else {
            update = paymentProps
        }
        this.setState({paymentInfo: update})
    }

    handleOnChangeAARCPaymentView(cashPaymentStatus: CASH_PAYMENT_STATUS): void {
        this.setState({cashPaymentStatus})
    }

    handleClickPreviousPage(): void {
        this.props.navigate(
            this.previousPageUrl,
            {
                replace: true,
                state: lsHelper.toObject(this.locationState),
            },
        )
    }

    async handleClickSubmit(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const {success, warnings} = await this.registerFacilityReservation()

            if (!success) {
                return
            }

            if (warnings.length > 0) {
                this.setState({
                    acceptedAllWarnings: warnings.length === 0,
                    registerWarnings: warnings.map(warning => {
                        return {
                            messageId: warning.messageId,
                            message: warning.message,
                        }
                    }),
                })
                return
            }

            const {cashPaymentStatus, ...rest} = this.locationState
            const newAdminReserveLocationState: AdminReserveLocationState = {
                ...rest,
                cashPaymentStatus: this.state.cashPaymentStatus,
            }

            const classificationString = this.props.classification
            this.props.navigate(
                `/admin/condominiums/${this.props.condominiumCd}/${classificationString}-reserve/complete`,
                {
                    replace: true,
                    state: {
                        ...lsHelper.toObject(newAdminReserveLocationState),
                        initialMessage: {
                            title: null,
                            messageLevel: MESSAGE_TYPE.INFORMATION,
                            detail: (this.locationState.reservationInfo.classification === ReservationClass.AGENT)
                                ? Messages.IFACA0011.toString() : Messages.IFACA0012.toString(),
                        },
                    },
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async registerFacilityReservation(): Promise<{ success: boolean, warnings: Warning[] }> {
        const {reservationInfo} = this.locationState
        if (reservationInfo.classification === ReservationClass.AGENT) {
            if (this.state.paymentInfo === null) {
                throw Error('支払金額の検索結果がありません。')
            }

            return await this.registerAgentReservation(reservationInfo, this.state.paymentInfo)
        } else {
            return await this.registerManagerReservation(reservationInfo)
        }
    }

    async registerAgentReservation(
        reservationInfo: AgentReservationInfo,
        paymentInfo: PaymentInformation,
    ): Promise<{ success: boolean, warnings: Warning[] }> {
        if (reservationInfo.userInfo === null) {
            throw Error('ユーザ情報がありません。')
        }

        const userId = reservationInfo.userInfo.sutekiNetId
        const userRidgeCd = reservationInfo.userInfo.ridgeCd
        const userRoomNumber = reservationInfo.userInfo.roomNumber
        const reserveOptions = reservationInfo.reservationOptionInfos
            ? reservationInfo.reservationOptionInfos.map(opt => {
                const {paymentType, ...rest} = opt
                return rest
            })
            : []
        const sharedUseRoomNumberList = reservationInfo.selectedRoomsOrDisabled || []
        const paymentInformationForReservation: PaymentInformationForReservation = {
            totalCharge: paymentInfo.totalCharge,
            items: paymentInfo.items.map(item => {
                const {paymentType, displayOrder, ...rest} = item
                return {...rest, paymentType: paymentType.value}
            }),
        }
        const cashPaymentStatusString = valueStringOrNull(this.state.cashPaymentStatus)
        const {acceptedAllWarnings, registerWarnings} = this.state

        if (reservationInfo.type === FacilityRentalType.OVERNIGHT) {
            return await this.registerAgentReservationForOvernight(
                reservationInfo.reservationTitle,
                reserveOptions,
                userId, userRidgeCd, userRoomNumber, sharedUseRoomNumberList,
                paymentInformationForReservation,
                cashPaymentStatusString,
                reservationInfo.startDate,
                reservationInfo.stayCount,
                acceptedAllWarnings ? registerWarnings.map(warning => warning.messageId) : [],
            )
        } else {
            return await this.registerAgentReservationForDuration(
                reservationInfo.reservationTitle,
                reserveOptions,
                userId, userRidgeCd, userRoomNumber, sharedUseRoomNumberList,
                paymentInformationForReservation,
                cashPaymentStatusString,
                reservationInfo.startDate,
                reservationInfo.startTime,
                reservationInfo.endDate,
                reservationInfo.endTime,
                acceptedAllWarnings ? registerWarnings.map(warning => warning.messageId) : [],
            )
        }
    }

    async registerAgentReservationForOvernight(
        reservationTitle: string,
        reserveOptions: ReservationOption[],
        userId: string,
        userRidgeCd: string,
        userRoomNumber: string,
        sharedUseRoomNumberList: SelectedUserList[],
        paymentInformationForReservation: PaymentInformationForReservation,
        cashPaymentStatus: Nullable<string>,
        startDate: string,
        stayCount: number,
        acceptedWarnings: string[],
    ): Promise<{ success: boolean, warnings: Warning[] }> {
        const newReservationForPost: NewAgentReservationForOvernight = {
            reservationTitle: reservationTitle,
            options: reserveOptions,
            userId, userRidgeCd, userRoomNumber, sharedUseRoomNumberList,
            paymentInformation: paymentInformationForReservation,
            cashPaymentStatus: cashPaymentStatus,
            startDate: startDate,
            stayCount: stayCount,
            acceptedWarnings: acceptedWarnings,
        }

        const result = await this.props.asyncCallFunction(
            this.props.reservationsRepoForAdmin.registerAgentReserveFacilityForOvernight.bind(
                this.props.reservationsRepoForAdmin,
                this.props.condominiumCd,
                this.locationState.facilityId,
                newReservationForPost,
            ),
            (httpErrorOrNetworkError) => {
                httpErrorOrNetworkError.broadcast(this.props.eventEmitter, true)
                return false
            },
        )

        return {
            success: result !== null,
            warnings: result
                ? result.informations.map(item => {
                    return {messageId: item.messageId, message: item.message}
                })
                : [],
        }
    }

    async registerAgentReservationForDuration(
        reservationTitle: string,
        reserveOptions: ReservationOption[],
        userId: string,
        userRidgeCd: string,
        userRoomNumber: string,
        sharedUseRoomNumberList: SelectedUserList[],
        paymentInformationForReservation: PaymentInformationForReservation,
        cashPaymentStatus: Nullable<string>,
        startDate: string,
        startTime: string,
        endDate: string,
        endTime: string,
        acceptedWarnings: string[],
    ): Promise<{ success: boolean, warnings: Warning[] }> {
        const newReservationForPost: NewAgentReservationForDuration = {
            reservationTitle: reservationTitle,
            options: reserveOptions,
            userId, userRidgeCd, userRoomNumber, sharedUseRoomNumberList,
            paymentInformation: paymentInformationForReservation,
            cashPaymentStatus: cashPaymentStatus,
            startDate: startDate,
            startTime: startTime,
            endDate: endDate,
            endTime: endTime,
            acceptedWarnings: acceptedWarnings,
        }

        const result = await this.props.asyncCallFunction(
            this.props.reservationsRepoForAdmin.registerAgentReserveFacilityForDuration.bind(
                this.props.reservationsRepoForAdmin,
                this.props.condominiumCd,
                this.locationState.facilityId,
                newReservationForPost,
            ),
            (httpErrorOrNetworkError) => {
                httpErrorOrNetworkError.broadcast(this.props.eventEmitter, true)
                return false
            },
        )

        return {
            success: result !== null,
            warnings: result
                ? result.informations.map(item => {
                    return {messageId: item.messageId, message: item.message}
                })
                : [],
        }
    }

    async registerManagerReservation(
        reservationInfo: ManagerReservationInfo,
    ): Promise<{ success: boolean, warnings: Warning[] }> {
        if (reservationInfo.userInfo === null) {
            throw Error('ユーザ情報がありません。')
        }
        const {acceptedAllWarnings, registerWarnings} = this.state

        const userId = reservationInfo.userInfo.adminId
        const reserveOptions = reservationInfo.reservationOptionInfos
            ? reservationInfo.reservationOptionInfos.map(opt => {
                const {paymentType, ...rest} = opt
                return rest
            })
            : []
        const sharedUseRoomNumberList = reservationInfo.selectedRoomsOrDisabled || []

        if (reservationInfo.type === FacilityRentalType.OVERNIGHT) {
            return await this.registerManagerReservationForOvernight(
                reservationInfo.reservationTitle,
                reserveOptions,
                userId,
                sharedUseRoomNumberList,
                reservationInfo.startDate,
                reservationInfo.stayCount,
                acceptedAllWarnings ? registerWarnings.map(warning => warning.messageId) : [],
            )
        } else {
            return await this.registerManagerReservationForDuration(
                reservationInfo.reservationTitle,
                reserveOptions,
                userId,
                sharedUseRoomNumberList,
                reservationInfo.startDate,
                reservationInfo.startTime,
                reservationInfo.endDate,
                reservationInfo.endTime,
                acceptedAllWarnings ? registerWarnings.map(warning => warning.messageId) : [],
            )
        }
    }

    async registerManagerReservationForOvernight(
        reservationTitle: string,
        reserveOptions: ReservationOption[],
        userId: string,
        sharedUseRoomNumberList: SelectedUserList[],
        startDate: string,
        stayCount: number,
        acceptedWarnings: string[],
    ): Promise<{ success: boolean, warnings: Warning[] }> {
        const newReservationForPost: NewManagerReservationForOvernight = {
            reservationTitle: reservationTitle,
            options: reserveOptions,
            userId,
            sharedUseRoomNumberList: sharedUseRoomNumberList,
            startDate: startDate,
            stayCount: stayCount,
            acceptedWarnings: acceptedWarnings,
        }

        const result: Nullable<ApiResponse<void>> = await this.props.asyncCallFunction(
            this.props.reservationsRepoForAdmin.registerManagerReserveFacilityForOvernight.bind(
                this.props.reservationsRepoForAdmin,
                this.props.condominiumCd,
                this.locationState.facilityId,
                newReservationForPost,
            ),
            (httpErrorOrNetworkError) => {
                httpErrorOrNetworkError.broadcast(this.props.eventEmitter, true)
                return false
            },
        )

        return {
            success: result !== null,
            warnings: result
                ? result.informations.map(item => {
                    return {messageId: item.messageId, message: item.message}
                })
                : [],
        }
    }

    async registerManagerReservationForDuration(
        reservationTitle: string,
        reserveOptions: ReservationOption[],
        userId: string,
        sharedUseRoomNumberList: SelectedUserList[],
        startDate: string,
        startTime: string,
        endDate: string,
        endTime: string,
        acceptedWarnings: string[],
    ): Promise<{ success: boolean, warnings: Warning[] }> {
        const newReservationForPost: NewManagerReservationForDuration = {
            reservationTitle: reservationTitle,
            options: reserveOptions,
            userId,
            sharedUseRoomNumberList: sharedUseRoomNumberList,
            startDate: startDate,
            startTime: startTime,
            endDate: endDate,
            endTime: endTime,
            acceptedWarnings: acceptedWarnings,
        }

        const result = await this.props.asyncCallFunction(
            this.props.reservationsRepoForAdmin.registerManagerReserveFacilityForDuration.bind(
                this.props.reservationsRepoForAdmin,
                this.props.condominiumCd,
                this.locationState.facilityId,
                newReservationForPost,
            ),
            (httpErrorOrNetworkError) => {
                httpErrorOrNetworkError.broadcast(this.props.eventEmitter, true)
                return false
            },
        )

        return {
            success: result !== null,
            warnings: result
                ? result.informations.map(item => {
                    return {messageId: item.messageId, message: item.message}
                })
                : [],
        }
    }

    changeAcceptedWarnings(acceptAll: boolean, _: AcceptedWarning[]): void {
        this.setState({acceptedAllWarnings: acceptAll})
    }

    render(): ReactNode {
        const disableRegisterButton: boolean =
            (this.state.paymentInfo === null) || (!this.state.acceptedAllWarnings)

        return (<>
            {
                this.props.classification === ReservationClass.AGENT
                    ? <Box height={'108px'} bgcolor={'#ffffff'} padding={'24px'}>
                        <AdminReserveStepper
                        classification={ReservationClass.AGENT}
                        step={AdminAgentReserveSteps.CONFIRM}
                        hasOption={this.hasOptions}
                    />
                    </Box>
                    : <Box height={'108px'} bgcolor={'#ffffff'} padding={'24px'}>
                        <AdminReserveStepper
                        classification={ReservationClass.MANAGER}
                        step={AdminManagerReserveSteps.CONFIRM}
                        hasOption={this.hasOptions}
                    />
                    </Box>
            }
            <Box mt={'10px'} mb={'10px'}>
                予約内容を確認してください。内容に間違いがなければ［施設を予約する］ボタンをクリックしてください。
            </Box>
            <PageMainArea>
                {
                    this.props.renderDetailsView({
                        data: {
                            type: 'confirm',
                            locationState: this.locationState,
                            hasOptions: this.hasOptions,
                            condominiumCd: this.props.condominiumCd,
                            navigate: this.props.navigate,
                            onChangeCashPaymentStatus: this.handleOnChangeAARCPaymentView,
                        },
                        observer: this,
                    })
                }
            </PageMainArea>
            <Box height={16}/>
            <NatAdminCommonAcceptedWarningComponent
                description={'居住者画面からの施設予約では制限している、以下の変更を行おうとしています。\n' +
                    '内容をご確認いただき、問題なければチェックしたうえで再度登録ボタンを押してください。'}
                warnings={this.state.registerWarnings}
                changeAcceptedWarnings={this.changeAcceptedWarnings}
                doOpenAnimation={true}
            />

            <PageFooterArea>
                <PageFotterAreaLeft>
                    <BackLink onClick={this.handleClickPreviousPage}>
                        {`${this.previousPageName}に戻る`}
                    </BackLink>
                </PageFotterAreaLeft>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        onClick={this.handleClickSubmit}
                        disabled={disableRegisterButton}
                    >
                        施設を予約する
                    </AdminPrimaryButton>
                </PageFotterAreaRight>
            </PageFooterArea>
        </>)
    }
}

export default createAdminPage(AdminReserveConfirmPage)
